/**
 * The inbox (Phase 4c, Task 23) slice of the wire — the read-only admin surface over
 * the inbound-email pipeline `wp_inbound_messages`/`wp_inbound_attachments` (stored
 * by Task 19, Phase 4a, with no HTTP surface until Task 23).
 *
 * Hand-written to mirror `openapi.yaml`'s `Inbox` tag, exactly as `recruiting.ts`'s
 * own header describes for its slice: transcribe what the screen reads, not the
 * whole contract, and this task does not touch `openapi.yaml`. A field the contract
 * marks `nullable` is `T | null` here, never omitted — the server sends the key with
 * a `null` value, it does not omit it (`PRODUCT.md` §4). `body_text` is likewise
 * `string | null` rather than optional, for the same reason.
 *
 * ── Two permissions, one screen — the split this file's own path builders keep ──
 *
 * Listing messages and reading one message's detail (body, matched candidate,
 * attachment metadata — never bytes) are `recruiting.view_candidates`, the same code
 * the candidates surface's reads use. **Reading an attachment's bytes is stronger**:
 * `recruiting.view_candidate_documents`, exactly the code the candidate-CV download
 * needs, because an inbound attachment may *be* a candidate's CV — a sensitive read
 * of PII, `x-wp-sensitive-access: true`, gated on a mandatory `X-Access-Reason`. This
 * client never decides which code a session holds; it only shapes the two different
 * requests those two codes reach.
 */

import { qs, type QueryValue } from './paths';
import type { CursorListEnvelope } from './types';

/* ──────────────────────────────────────────────────────────── inbound messages ── */

/**
 * `InboxParseStatus` — `wp_inbound_messages.parse_status` verbatim (migration 044).
 * `needs_human` means the Phase 4a whitelist parser could not make sense of some
 * part of the message; it is still stored, never dropped, for a person to review.
 */
export const INBOX_PARSE_STATUSES = ['parsed', 'partial', 'needs_human'] as const;

export type InboxParseStatus = (typeof INBOX_PARSE_STATUSES)[number];

/**
 * `InboxMessageSummary` of `openapi.yaml` — one `GET /inbox/messages` list row.
 * `required: [id, parse_status, attachment_count]`; every other member is nullable
 * on the wire and so `T | null` here — the server sends the key with a `null`
 * value, it does not omit it. Never `body_text` — that is the separate one-message
 * read below.
 */
export interface InboxMessageSummary {
  readonly id: string;
  readonly from_address: string | null;
  readonly subject: string | null;
  readonly received_at: string | null;
  readonly parse_status: InboxParseStatus;
  readonly candidate_id: string | null;
  readonly application_id: string | null;
  /** How many `wp_inbound_attachments` rows this message has, stored or not. */
  readonly attachment_count: number;
}

/** `InboxMessageListResponse` — a cursor page, newest first (by `id`, descending). */
export type InboxMessageListResponse = CursorListEnvelope<InboxMessageSummary>;

/**
 * `InboxAttachment` of `openapi.yaml` — an inbound attachment's metadata, never its
 * bytes and never a download URL. `required: [id, message_id, stored]`.
 *
 * `stored: false` means the Phase 4a whitelist parser refused it (an unknown
 * declared type, a type/bytes mismatch, or an over-cap size) or object storage was
 * not configured at poll time; `rejected_reason` says which. The bytes, when
 * `stored` is `true`, are read through the separate, access-logged download —
 * a `stored: false` row is never offered as a download here.
 */
export interface InboxAttachment {
  readonly id: string;
  readonly message_id: string;
  readonly filename: string | null;
  readonly content_type: string | null;
  readonly size_bytes: number | null;
  readonly stored: boolean;
  readonly rejected_reason: string | null;
}

/**
 * `InboxMatchedCandidate` of `openapi.yaml` — the minimal candidate summary a
 * message detail embeds. `required: [id, full_name]`.
 */
export interface InboxMatchedCandidate {
  readonly id: string;
  readonly full_name: string;
  readonly email: string | null;
}

/**
 * `InboxMessageDetail` of `openapi.yaml` — `GET /inbox/messages/{id}`: the message
 * with `body_text` (already sanitized and stored by the Phase 4a parser), the
 * matched candidate's summary when one was found, and every attachment's metadata.
 * `required: [id, parse_status, attachments]`. `candidate` is `null` when the
 * sender never matched a candidate — a lead to triage, not an error.
 */
export interface InboxMessageDetail {
  readonly id: string;
  readonly from_address: string | null;
  readonly subject: string | null;
  readonly body_text: string | null;
  readonly received_at: string | null;
  readonly parse_status: InboxParseStatus;
  readonly candidate_id: string | null;
  readonly application_id: string | null;
  readonly candidate: InboxMatchedCandidate | null;
  readonly attachments: readonly InboxAttachment[];
}

/* ──────────────────────────────────────────────────────────────── path builders ── */

export const INBOX_MESSAGES_PATH = '/api/v1/inbox/messages';

/**
 * `GET /inbox/messages` — cursor-paged, newest first, scoped to the caller's active
 * company by the server and never by a query parameter (API_CONTRACT §7.4 rule 1).
 * `candidate_id` narrows to the messages matched to one candidate; `status` narrows
 * to one `parse_status`; `cursor` is the previous page's `page.next_cursor`, opaque
 * and forwarded unread.
 */
export function inboxMessagesPath(query: {
  readonly limit: number;
  readonly candidateId?: string | undefined;
  readonly status?: InboxParseStatus | undefined;
  readonly cursor?: string | undefined;
}): string {
  const params: Readonly<Record<string, QueryValue>> = {
    limit: query.limit,
    candidate_id: query.candidateId,
    status: query.status,
    cursor: query.cursor,
  };
  return `${INBOX_MESSAGES_PATH}${qs(params)}`;
}

/**
 * `GET /inbox/messages/{id}` — one message by id. A message of another company, or
 * an id that does not exist at all, answers `404` either way — a caller cannot tell
 * the two apart.
 */
export function inboxMessagePath(id: string): string {
  return `${INBOX_MESSAGES_PATH}/${encodeURIComponent(id)}`;
}

/**
 * `GET /inbox/messages/{id}/attachments/{attachmentId}/download` — the one
 * sensitive, access-logged read of the inbox surface. Requires the stronger
 * `recruiting.view_candidate_documents` (holding `recruiting.view_candidates`,
 * enough to reach the message and the attachment's metadata, is deliberately not
 * enough to read the bytes) and a mandatory `X-Access-Reason`, and `302`s to a
 * short-lived presigned GET URL that the browser follows. An attachment the
 * whitelist parser refused, or that never reached object storage, answers `409
 * WORKFORCE_CONFLICT` rather than a broken link.
 */
export function inboxAttachmentDownloadPath(messageId: string, attachmentId: string): string {
  return `${inboxMessagePath(messageId)}/attachments/${encodeURIComponent(attachmentId)}/download`;
}
